import { Optional, Injectable } from '@angular/core';
import { ɵgetDOM } from '@angular/platform-browser';
import 'zone.js';

export class CookieOptions {
	path: string;
	domain: string;
	expires: string | Date;
	secure: boolean;

	constructor({ path, domain, expires, secure }: CookieOptionsArgs) {
		this.path = path != null ? path : null;
		this.domain = domain != null ? domain : null;
		this.expires = expires != null ? expires : null;
		this.secure = secure != null ? secure : false;
	}

	merge(options?: CookieOptionsArgs): CookieOptions {
		return new CookieOptions(<CookieOptionsArgs>{
			path: (options != null && options.path != null) ? options.path : this.path,
			domain: (options != null && options.domain != null) ? options.domain : this.domain,
			expires: (options != null && options.expires != null) ? options.expires : this.expires,
			secure: (options != null && options.secure != null) ? options.secure : this.secure,
		});
	}
}

@Injectable()
export class BaseCookieOptions extends CookieOptions {
	constructor() { super(<CookieOptionsArgs>{ path: window ? ɵgetDOM().getBaseHref(window.document) : '' }); }
}

export interface CookieOptionsArgs {
	path?: string;
	domain?: string;
	expires?: string | Date;
	secure?: boolean;
	httpOnly?: boolean;
}

export abstract class Cookie {
	abstract get(key: string): string;
	abstract put(key: string, value: string, options?: CookieOptionsArgs);
	abstract getAll(): Object;
	abstract remove(key: string, options?: CookieOptionsArgs): void;
}

export class CookieNode implements Cookie {
	private _cache = {};
	constructor() {
		this._cache = this.req('req').cookies;
	}
	getAll(): Object {
		return this.req('req').cookies;
	}
	get(key: string): string {
		if (this._cache[key]) {
			return this._cache[key];
		}
		return this.req('req').cookies[key];
	}
	put(key: string, value: string, options?: CookieOptionsArgs) {
		this._cache[key] = value;
		this.req('res').cookies[key] = value;
	}
	remove(key: string, options?: CookieOptionsArgs): void {
		delete (this._cache[key]);
		delete (this.req('res').cookies[key]);
	}

	private req(key: string) {
		return Zone.current.get(key) || {};
	}
}

@Injectable()
export class CookieBrowser implements Cookie {
	constructor(@Optional() private _defaultOptions: CookieOptions) { }

	get(key: string): string { return (<any>this._cookieReader())[key]; }

	getAll(): Object { return <any>this._cookieReader(); }
	put(key: string, value: string, options?: CookieOptionsArgs) {
		this._cookieWriter()(key, value, options);
	}


	remove(key: string, options?: CookieOptionsArgs): void {
		this._cookieWriter()(key, undefined, options);
	}

	removeAll(): void {
		let cookies = this.getAll();
		Object.keys(cookies).forEach(key => { this.remove(key); });
	}

	private _cookieReader(): Object {
		let rawDocument = document;
		let lastCookies = {};
		let lastCookieString = '';
		let that = this;

		let cookieArray: string[], cookie: string, i: number, index: number, name: string;
		let currentCookieString = rawDocument.cookie || '';
		if (currentCookieString != lastCookieString) {
			lastCookieString = currentCookieString;
			cookieArray = lastCookieString.split('; ');
			lastCookies = {};

			for (i = 0; i < cookieArray.length; i++) {
				cookie = cookieArray[i];
				index = cookie.indexOf('=');
				if (index > 0) {  // ignore nameless cookies
					name = that._safeDecodeURIComponent(cookie.substring(0, index));
					if ((<any>lastCookies)[name] == null) {
						(<any>lastCookies)[name] = that._safeDecodeURIComponent(cookie.substring(index + 1));
					}
				}
			}
		}
		return lastCookies;
	}

	private _cookieWriter() {
		let self = this,
			rawDocument = document;

		return function (name: string, value: string, options?: CookieOptionsArgs) {
			rawDocument.cookie = self._buildCookieString(name, value, options);
		};
	}

	private _safeDecodeURIComponent(str: string) {
		try {
			return decodeURIComponent(str);
		} catch (e) {
			return str;
		}
	}

	private _buildCookieString(name: string, value: string, options?: CookieOptionsArgs): string {
		let cookiePath = '/',
			path: string,
			expires: any,
			defaultOpts = this._defaultOptions || new CookieOptions(<CookieOptionsArgs>{ path: cookiePath }),
			opts: CookieOptions = this._mergeOptions(defaultOpts, options);

		expires = opts.expires;

		if (value == null) {
			expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
			value = '';
		}

		if (typeof expires == 'string') {
			expires = new Date(expires);
		}

		let str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
		str += opts.path ? ';path=' + opts.path : '';
		str += opts.domain ? ';domain=' + opts.domain : '';
		str += expires ? ';expires=' + expires.toUTCString() : '';
		str += opts.secure ? ';secure' : '';

		let cookieLength = str.length + 1;
		if (cookieLength > 4096) {
			console.warn(`Cookie \'${name}\' possibly not set or overflowed because it was too large (${cookieLength} > 4096 bytes)!`);
		}

		return str;
	}

	private _mergeOptions(defaultOpts: BaseCookieOptions, providedOpts?: CookieOptionsArgs): CookieOptions {
		if (providedOpts != null) {
			return defaultOpts.merge(new CookieOptions(providedOpts));
		}
		return defaultOpts;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./app/core/cookies.ts